/**
 * Firebase Web 初期化（Web ブラウザでの Google Sign-In 用）。
 *
 * Capacitor ネイティブ / LINE Mini App では Firebase Auth は使わず、
 * 各プラットフォームのネイティブ SDK（Capacitor Social Login, LIFF）を優先する。
 *
 * 実キー未設定時は `getFirebaseAuth()` が null を返すようにして、
 * 呼び出し側が「Web Google サインインは無効」として分岐できるようにする。
 *
 * 関連:
 *   - `src/config/firebase.ts` — env から設定を読む
 *   - `src/services/auth.ts` — Firebase Auth を使う signInWithGoogle(web ブランチ)
 *   - `docs/AUTH_SETUP.md` — 実キーの入手手順
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import {
  getAuth,
  type Auth,
} from 'firebase/auth'
import { getFirebaseWebConfig } from '../config/firebase'

let cachedApp: FirebaseApp | null = null
let cachedAuth: Auth | null = null
let initialized = false

/**
 * Firebase App を遅延初期化する。
 * 環境変数未設定なら null を返し、呼び出し側が明示的にスキップできるようにする。
 */
export function getFirebaseApp(): FirebaseApp | null {
  if (initialized) return cachedApp

  const cfg = getFirebaseWebConfig()
  if (!cfg) {
    initialized = true
    cachedApp = null
    return null
  }

  // すでに他モジュールが initializeApp していれば再利用する
  const existing = getApps()[0]
  if (existing) {
    cachedApp = existing
  } else {
    cachedApp = initializeApp(cfg)
  }
  initialized = true
  return cachedApp
}

/**
 * Firebase Auth インスタンスを取得する。
 * Firebase 設定未完了時は null を返す。
 */
export function getFirebaseAuth(): Auth | null {
  if (cachedAuth) return cachedAuth
  const app = getFirebaseApp()
  if (!app) return null
  cachedAuth = getAuth(app)
  return cachedAuth
}

/**
 * Firebase Web が利用可能か（env に 必須 3 項目が入っているか）を返す。
 */
export function isFirebaseWebReady(): boolean {
  return getFirebaseAuth() !== null
}
